import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { NewUploadEnum } from '../../utils/enums';
import authorize from '../../utils/auth';
import AuthorizedResource from '../authorization/AuthorizedResource';
import DOCUMENTS_CONTROLLER from '../../controllers/documents'
import getClientConfig from '../../utils/getClientConfig';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import { getAccessToken } from '../../utils/tokenStorage';

// Helper function to create URL parameters
const createUrlParams = (props, enableAvalaraIdentityJwt, initialAccessToken) => {
    const baseParams = [
        ['tenantId', props.rdiTenantId],
        ['featureFlag', 'false'],
        ['enableVatPlatformProcessing', props.enableVatPlatformProcessing]
    ];

    if (enableAvalaraIdentityJwt) {
        return new URLSearchParams([
            ...baseParams,
            ['enableAvalaraIdentityJwt', enableAvalaraIdentityJwt],
            ['jwtAccessToken', initialAccessToken]
        ]);
    }

    return new URLSearchParams([
        ...baseParams,
        ['token', props.auiAt]
    ]);
};

// Helper function to create localStorage listener
const createLocalStorageListener = (onChange) => {
    const storageListener = (event) => {
        if (event.key === 'access_token') {
            onChange(event.newValue);
        }
    };
    window.addEventListener('storage', storageListener);
    return () => window.removeEventListener('storage', storageListener);
};

// Helper function to create cookie listener
const createCookieListener = (onChange) => {
    const changeListener = (event) => {
        const foundCookie = event.changed.find((cookie) => cookie.name === "access_token");
        if (foundCookie) {
            onChange(foundCookie.value);
            return;
        }

        const deletedCookie = event.deleted.find((cookie) => cookie.name === "access_token");
        if (deletedCookie) {
            onChange(null);
        }
    };
    cookieStore.addEventListener("change", changeListener);
    return () => cookieStore.removeEventListener("change", changeListener);
};

// Helper function to listen for storage changes
const listenForStorageChange = (enableLocalStorageForTokens, onChange) => {
    if (enableLocalStorageForTokens) {
        return createLocalStorageListener(onChange);
    }
    return createCookieListener(onChange);
};

// Helper function to set JWT token
const setNewJwtToken = async (token, props, enableAvalaraIdentityJwt, initialAccessToken, newUploadPageUrl, iframePath, vatMapperId) => {
    const iframe = document.getElementById(vatMapperId);
    if (!iframe || !token) return;

    const urlParams = createUrlParams(props, enableAvalaraIdentityJwt, initialAccessToken);
    const iframeSrc = `${newUploadPageUrl}${iframePath}?${urlParams.toString()}`;
    iframe.contentWindow.postMessage({ type: "AUTH_TOKEN", token }, iframeSrc);
};

// Helper function to initialize accordion state
const initializeAccordionState = (cookies, setIsExpanded) => {
    const cookieState = cookies.get(NewUploadEnum.ACCORDION_STATE);
    if (cookieState !== undefined) {
        setIsExpanded(cookieState === NewUploadEnum.EXPANDED);
    } else {
        setIsExpanded(true);
        cookies.set(NewUploadEnum.ACCORDION_STATE, NewUploadEnum.EXPANDED);
    }
};

// Helper function to handle accordion toggle
const handleAccordionToggle = (isExpanded, setIsExpanded, cookies) => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    cookies.set(NewUploadEnum.ACCORDION_STATE, newExpanded ? NewUploadEnum.EXPANDED : NewUploadEnum.COLLAPSED);
};

// Helper function to render accordion header
const renderAccordionHeader = (isExpanded) => {
    const infoTrigger = <><LanguageStringResolver className="m15" string='uploadInformation' /><s-icon name="chevron-down"></s-icon></>;
    const infoTriggerOff = <><LanguageStringResolver className="m15" string='uploadInformation' /><s-icon name="chevron-up"></s-icon></>;
    return isExpanded ? infoTriggerOff : infoTrigger;
};

// Helper function to render accordion content
const renderAccordionContent = (enableClearingText) => (
    <div>
        <p className="small-font ml20">
            <LanguageStringResolver string='uploadFlowInfo' /><br />
            <LanguageStringResolver string='newFileNameRestrictions' />
            <code><LanguageStringResolver string='fileNameAllowedSpecialCharacters' /></code><br />
            <LanguageStringResolver string='newRightToUse' /><br />
            <LanguageStringResolver string='newDownloadGenericFullTemplateText' /><br />
            <a data-test="button-download-full-template" className="button secondary m15" href="/full-template.xls"><LanguageStringResolver string='downloadGenericFullTemplateBtn' /></a>
            <a data-test="button-download-gl-template" className="button secondary m15" href="/gldata-template.xls"><LanguageStringResolver string='downloadGenericGLTemplateBtn' /></a>
            {enableClearingText && <a data-test="button-download-ct-template" className="button secondary m15" href="/clearing-template.xls"><LanguageStringResolver string='downloadGenericCTTemplateBtn' /></a>}
        </p>
    </div>
);

// Helper function to check if component should render
const shouldRender = (props, enableAvalaraIdentityJwt, initialAccessToken) => {
    return props.rdiTenantId && ((enableAvalaraIdentityJwt && initialAccessToken) || props.auiAt);
};

const showAccordionAndProcessedDocument = (pageName) => {
    return pageName === 'NewUploadPage';
};

const IframePage = (props) => {
    const { iframePath = '', pageName = '' } = props;

    const [isExpanded, setIsExpanded] = useState(false);
    const cookies = new Cookies();
    const initialAccessToken = getAccessToken();
    const { newUploadPageUrl, enableAvalaraIdentityJwt, enableLocalStorageForTokens } = getClientConfig();
    const vatMapperId = "vat-mapper-i-frame";

    useEffect(() => {
        if (enableAvalaraIdentityJwt) {
            const unsubscribe = listenForStorageChange(enableLocalStorageForTokens, async (newToken) => {
                await setNewJwtToken(newToken, props, enableAvalaraIdentityJwt, initialAccessToken, newUploadPageUrl, iframePath, vatMapperId);
            });

            return () => {
                unsubscribe();
            };
        }
    }, []);

    useEffect(() => {
        props.doLoadRdiTenantId();

        if (!enableAvalaraIdentityJwt) {
            props.doLoadAuiAt();
        }

        if (showAccordionAndProcessedDocument(pageName)) {
            initializeAccordionState(cookies, setIsExpanded);
        }
    }, []);

    const toggleAccordion = () => {
        handleAccordionToggle(isExpanded, setIsExpanded, cookies);
    };

    const urlParams = createUrlParams(props, enableAvalaraIdentityJwt, initialAccessToken);
    const iframeSrc = `${newUploadPageUrl}${iframePath}?${urlParams.toString()}`;
    const accordionHeader = renderAccordionHeader(isExpanded);
    const enableClearingText = getClientConfig()?.featureFlags?.EnableClearingText;
    const enableUploadedDocumentsPage = getClientConfig()?.featureFlags?.EnableUploadedDocumentsPage;

    if (!shouldRender(props, enableAvalaraIdentityJwt, initialAccessToken)) {
        return null;
    }

    return (
        <>
            <div className="content-area">
                {showAccordionAndProcessedDocument(pageName) && (
                    <div>
                        <hr className="ml20 mr35" />
                        <button className='accordion-button ml5' onClick={toggleAccordion}>
                            {accordionHeader}
                        </button>
                        {isExpanded && renderAccordionContent(enableClearingText)}
                        <hr className="ml20 mr35 mb0" />
                    </div>
                )}
                <iframe id={vatMapperId} className={pageName === 'NewUploadPage' ? 'iframe' : 'iframe_template'} data-testid="new-upload-iframe" src={iframeSrc} />
                {showAccordionAndProcessedDocument(pageName) && (
                    enableUploadedDocumentsPage
                        ? <Link data-test="button-uploaded-document" className="button secondary m15 mt4 ml35" to="/uploadedDocuments"><LanguageStringResolver string='importTransactions_viewAllUploaded' /></Link>
                        : <Link data-test="button-processed-document" className="button secondary m15 mt4 ml35" to="/ProcessedDocuments"><LanguageStringResolver string='importTransactions_viewAll' /></Link>
                )}
            </div>
        </>
    );
};

function mapDispatchersToProps(dispatch) {
    return {
        doLoadRdiTenantId: () => {
            dispatch(DOCUMENTS_CONTROLLER.doLoadRdiTenantId());
        },
        doLoadAuiAt: () => {
            dispatch(DOCUMENTS_CONTROLLER.doLoadAuiAt());
        }
    }
}

function mapStateToProps(state) {
    return {
        rdiTenantId: state.documents?.rdiTenantId,
        auiAt: state.documents?.auiAt,
        enableVatPlatformProcessing: state.featureFlag?.data?.EnableVatPlatformProcessing
    };
}

export default authorize({
    name: 'IframePage',
    resource: connect(mapStateToProps, mapDispatchersToProps)(IframePage)
}, AuthorizedResource, {
    showNotAuthorized: true
}); 